import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ConversionData } from 'src/app/models/conversion.model';
import { NbStepperComponent } from '@nebular/theme';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { SharedService } from 'src/app/shared/services/shared.service';
import { User } from 'src/app/models/users.model';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { take, filter, timeout, catchError } from 'rxjs/operators';
import { UploadTaskSnapshot } from '@angular/fire/compat/storage/interfaces';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { DocparserParser } from 'src/app/models/docparser-doc.model';
import { Conversion } from 'src/app/models/conversion.model';
import { UntypedFormControl } from '@angular/forms';
import { CONVERSION_TIMEOUT_MS } from 'src/app/shared/constants';
import { TimeoutError } from 'rxjs';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
const firestore = firebase.firestore;
@Component({
  selector: 'app-convert-2',
  templateUrl: './convert-2.component.html',
  styleUrls: ['./convert-2.component.scss'],
})
export class Convert2Component implements OnInit, OnDestroy {
  @Input()
  conversionData: ConversionData;
  @Input()
  stepper: NbStepperComponent;

  stepperControl = new UntypedFormControl(); // Controls wether we can go to next step or not

  user: User;
  uploadPercent = 0;
  loading = true;
  loadingText = 'Conversion starting';
  doneLoadingObj: {
    healthy: boolean;
    details?: string;
  } = {
    healthy: null,
  } as any;
  conversion: Conversion[];

  hintTextInterval;

  hintTextArray = [
    `Note that this may take up to 2 minutes.`,
    `You will receive an email once your documents have been converted. You can at anytime view all your previous statements
    conversions in the Statement History section.`,
    `If we are unable to correctly convert your statements, your documents will be sent through to our back office. We will try to
    get back to you within a few hours with the corrected conversion, or a refund.`,
    `We will let you know if there are minor errors during the conversion. Feel free to download the converted csv and
    view the details for any simple corrections. Our back office will still receive these files and attempt to correct them.`,
    `Not receiving your converted files through email? Ensure your contact details are correct under My Profile. Otherwise they may
    be hiding in you emails spam folder.`,
    `Ensure that scanned statement are of high quality as poor quality scans may not work.`,
    `Uploading multiple statements from the same bank will be faster than converting the files separately.`,
  ];

  hintText = this.hintTextArray[0];

  constructor(
    private storage: AngularFireStorage,
    private ss: SharedService,
    private afs: AngularFirestore,
    private http: HttpClient,
  ) {}

  async ngOnInit() {
    this.user = await this.ss.getUser();
    this.stepperControl.setErrors({ disabled: true }); // Prevent clicking steps or next until we set control valid
    this.stepper.selected.stepControl = this.stepperControl;
    this.uploadFiles(this.conversionData.files);
    this.shuffle(this.hintTextArray);
    this.hintTextInterval = setInterval(() => {
      const currentIndex = this.hintTextArray.findIndex((a) => a === this.hintText);
      let nextIndex = currentIndex + 1;
      if (nextIndex >= this.hintTextArray.length) {
        nextIndex = 0;
      }
      this.hintText = this.hintTextArray[nextIndex];
    }, 11000);
  }

  ngOnDestroy() {
    if (this.hintTextInterval) {
      clearInterval(this.hintTextInterval);
    }
  }

  shuffle(a) {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  async uploadFiles(files: ConversionData['files']) {
    try {
      const isBatch = files.length > 1;
      const batchUid = this.afs.createId();
      this.conversion = await Promise.all(
        files
          .sort((a, b) => a.file.size - b.file.size)
          .map(async (file, i) => {
            //  --- Uploading to storage

            const conversionUid = this.afs.createId();

            const nameArray = file.file.name.split('.');
            const extension = nameArray[nameArray.length - 1];
            const ref = this.storage.ref(
              `Statements/${this.user.uid}/${file.file.name.substring(0, file.file.name.length - 4)} ${conversionUid}.${extension}`,
            );
            const task = ref.put(file.file);

            if (i === files.length - 1) {
              // Only listen to last file which is largest due to sort
              task.percentageChanges().subscribe((val) => {
                this.uploadPercent = val;
              });
            }

            this.loadingText = 'Uploading files';

            const completedTask = (await task.then()) as UploadTaskSnapshot;

            const downloadUrl = await ref.getDownloadURL().pipe(take(1)).toPromise();

            const fileDoc: Conversion = {
              uid: conversionUid,
              userUid: this.user.uid,
              userName: this.user.name,
              parserId: this.conversionData.parserId,
              parserDisplay: this.conversionData.parserDisplay,
              batchUid: batchUid,
              isBatch: isBatch,
              fileName: file.file.name,
              downloadUrl: downloadUrl,
              pageCount: file.pageCount,
              uploadDate: firestore.Timestamp.now(),
              countryCode: this.user.countryCode,
              lastAdminUpdateUserName: null,
              lastAdminUpdateUserUid: null,
              isSensitive: !!this.user.isSensitive,
            };
            await firestore().doc(`files/${fileDoc.uid}`).set(fileDoc, { merge: true });

            //  --- Starting Docparser

            type ConvertRes = { id: string };

            const body = {
              downloadUrl: downloadUrl,
              parserId: this.conversionData.parserId,
              userUid: this.user.uid,
              fileId: conversionUid,
            };

            const url =
              this.conversionData.parserId === 'aiparser'
                ? `${environment.functionsRootUrl}/aiparser-convert/convert`
                : `${environment.functionsRootUrl}/dp-convert/convert`;

            // Credits are deducted server-side when the conversion starts
            const convertResult: ConvertRes = (await this.http.post(url, body).toPromise()) as ConvertRes;

            fileDoc.docparserDocID = convertResult.id;
            await firestore().doc(`files/${fileDoc.uid}`).update({ docparserDocID: convertResult.id });

            //  --- Docparser doing it thing

            this.loadingText = 'Converting statements';
            setTimeout(() => {
              this.loadingText = 'This usually take around 30+ seconds';
            }, 12000);

            const updatedFileDoc = (await this.listenForDocparserResult(fileDoc)) as Conversion;
            return updatedFileDoc;
          }),
      );
      this.loadingText = 'Done converting';
      this.completionHandler();
    } catch (error) {
      console.error(error);
      this.completionHandler(error);
    }
  }

  async listenForDocparserResult(fileDoc: Conversion) {
    try {
      const dbDoc = await this.afs
        .doc(`files/${fileDoc.uid}`)
        .valueChanges()
        .pipe(timeout(CONVERSION_TIMEOUT_MS))
        // Cloud function saves healthCheck data when complete
        .pipe(
          filter((snap: Conversion) => snap.healthCheck != null),
          take(1),
        )
        .toPromise();
      return dbDoc;
    } catch (error) {
      console.warn(error);
      if (error instanceof TimeoutError) {
        return (await firestore().doc(`files/${fileDoc.uid}`).get()).data();
      } else {
        throw error;
      }
    }
  }

  completionHandler(error?) {
    this.loading = false;
    this.conversionData.convertedFiles = this.conversion;
    if (error) {
      this.doneLoadingObj.healthy = false;
      this.doneLoadingObj.details = error.error?.message || error.message;
    } else if (this.conversion.some((a) => !a.healthCheck || !a.healthCheck.healthy)) {
      this.doneLoadingObj.healthy = false;
      this.doneLoadingObj.details = `We had issues converting ${this.conversion
        .filter((a) => !a.healthCheck || !a.healthCheck.healthy)
        .map((a) => a.fileName)
        .join(', ')}`;
    } else {
      // Healthy
      this.doneLoadingObj.healthy = true;
    }
    this.stepperControl.setErrors(null);
    this.stepper.next();
  }
}
